import './CodeEventList.scss';

import { ContentCopy, Remove } from '@mui/icons-material';
import { Collapse, IconButton, Stack } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { CodeEvent, CodeEventFilters } from 'domain/codeEvent/CodeEvent';
import { Translate } from 'domain/translation/Translation.repository';
import { formatDate } from 'helper/date.helper';
import { CreateCodeEventForm } from 'primary/codeEvent/form/CreateCodeEventForm';
import { CoupleButtonValidCancel } from 'primary/Components/CoupleButtonValidCancel/CoupleButtonValidCancel';
import { sendEventToastMessage } from 'primary/Components/Toast/Toast.helper';
import { useContextDependency } from 'primary/hooks/useContextDependency';
import { useFetchWithFilterPage } from 'primary/hooks/useFetchWithFilterPage';
import { useTranslate } from 'primary/hooks/useTranslate';
import React, { useState } from 'react';

const formatCodeEventToRowData = (codeEvents: CodeEvent[], t: Translate) => {
  return codeEvents.map((code) => ({
    id: code.id,
    code: code.code,
    expireDate: formatDate(code.expireDate, 'readable(long year)'),
    valid: !code.expired ? 'Oui' : 'Utilisé',
    meta: code,
  }));
};

interface CodeEventListProps {
  eventId: number;
}

export const CodeEventList = ({ eventId }: CodeEventListProps) => {
  const t = useTranslate();
  const { codeEventRepository } = useContextDependency();
  const [addCodeDisplay, setAddCodeDisplay] = useState<boolean>(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState<CodeEvent | undefined>();
  const [isDeleteLaoding, setIsDeleteLaoding] = useState<boolean>(false);
  const {
    fetchedResource,
    goToPage,
    loading,
    activeFilters,
    setFilters,
    changeNbPerPage,
    fetch,
    nextPage,
    retry,
    addToCurrentPage,
    allContents,
  } = useFetchWithFilterPage<CodeEvent, CodeEventFilters>({
    filters: {},
    staticFilters: { eventId: eventId },
    callApi: (filters, page) => codeEventRepository.search(filters, page, 10),
    keyStoreFilters: 'code-event-list',
  });

  const columns: GridColDef[] = [
    {
      field: 'code',
      headerName: t('event.codeEvent.datatable.code'),
      flex: 2,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'expireDate',
      headerName: t('event.codeEvent.datatable.expireDate'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'valid',
      headerName: t('event.codeEvent.datatable.expired'),
      flex: 1,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'meta',
      headerName: t('event.codeEvent.datatable.actions'),
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      flex: 2,
      renderCell: (value: GridRenderCellParams<CodeEvent>) => {
        return (
          <div className={'-actions'}>
            <IconButton
              disabled={!value?.value}
              title={t('event.codeEvent.datatable.copyCode')}
              onClick={() =>
                navigator.clipboard.writeText(value?.value?.code).then(() => {
                  sendEventToastMessage('Copié avec succès', 'success');
                })
              }
              color={'primary'}
            >
              <ContentCopy />
            </IconButton>
            <IconButton
              disabled={!value?.value}
              title={t('event.codeEvent.datatable.delete')}
              onClick={() => setIsConfirmDeleteOpen(value?.value)}
              color={'primary'}
            >
              <Remove />
            </IconButton>
          </div>
        );
      },
    },
  ];

  return (
    <div className={'codeEventList'}>
      <Collapse className={'-collapseCreateCode'} in={!isConfirmDeleteOpen}>
        <CreateCodeEventForm
          key={addCodeDisplay?.toString()}
          eventId={eventId}
          onSubmitted={(codeEvent) => {
            if (codeEvent) {
              retry();
            }
          }}
          onCancel={() => setAddCodeDisplay(false)}
        />
      </Collapse>
      <Collapse className={'-confirmDelete'} in={!!isConfirmDeleteOpen}>
        <span>
          Êtes-vous sur de vouloir supprimer le code {isConfirmDeleteOpen?.code} ?{' '}
        </span>
        <CoupleButtonValidCancel
          loading={isDeleteLaoding}
          onClickValid={() => {
            setIsDeleteLaoding(true);
            codeEventRepository.delete(isConfirmDeleteOpen?.id as number).finally(() => {
              setIsDeleteLaoding(false);
              setIsConfirmDeleteOpen(undefined);
              retry();
            });
          }}
          onClickCancel={() => setIsConfirmDeleteOpen(undefined)}
        />
      </Collapse>

      <div className={'codeEventList__list'}>
        <DataGrid
          disableColumnFilter
          disableColumnMenu
          disableColumnSelector
          disableDensitySelector
          disableEval
          disableVirtualization
          pagination
          disableRowSelectionOnClick
          getRowId={(val) => val.code}
          paginationMode={'server'}
          rowCount={fetchedResource?.totalElementsCount}
          paginationModel={{
            pageSize: fetchedResource?.pageSize || 0,
            page: fetchedResource?.currentPage || 0,
          }}
          onPaginationModelChange={({ pageSize, page }) => {
            if (pageSize !== fetchedResource?.pageSize) {
              changeNbPerPage(pageSize);
            }
            goToPage(page);
          }}
          loading={loading}
          className={'-parameters'}
          autoHeight
          sx={{ color: 'white' }}
          slots={{
            // @ts-ignore
            noRowsOverlay: () => (
              <Stack height="100%" alignItems="center" justifyContent="center">
                {t('event.teams.datatable.noContentLabel')}
              </Stack>
            ),
          }}
          rows={
            fetchedResource?.content
              ? formatCodeEventToRowData(fetchedResource?.content, t)
              : []
          }
          columns={columns}
        />
      </div>
    </div>
  );
};
